import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Tabs,
  Group,
  Badge,
  Loader,
  Center,
  Box,
  Modal,
  Stack,
  Button,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { CircleBackButton } from '../../components/common/CircleBackButton';
import {
  coachPortalApi,
  type CoachCohortDetail,
  type SessionNeedingAttendance,
} from '../../services/api/coachPortal';
import { sessionsApi } from '../../services/api/sessions';
import {
  AttendanceNeededBanner,
  FeedbackNeededBanner,
  PostSessionModal,
  AddHuddleModal,
  HuddleDetailModal,
  type HuddleParticipantOption,
} from './components';
import { COHORT_STATUS_CONFIG } from '../../config/statusConfig';
import { formatDateOnlyRange } from '../../utils/formatters';
import type { CoachHuddle } from '../../types';
import {
  SessionsTabContent,
  ResourcesTabContent,
  CoachParticipantsTabContent,
  type CoachParticipantsView,
} from './cohort-detail';
import { SurveyResultsModal } from '../cohorts/components/sessions';
import { CoachFeedbackPane } from './components/CoachFeedbackPane';
import { useAuthStore } from '../../stores/auth';
import { useFeatureFlag } from '../../stores/featureFlags';
import { FeaturesDisabledPage } from '../FeaturesDisabledPage';

export function CoachCohortDetailPage() {
  const { cohortId } = useParams<{ cohortId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((state) => state.user);
  const isPartnerCoach = user?.coach_type === 'partner';
  // MED-1152: gates routing completed Live Session Delivery sessions to the
  // post-session page instead of the legacy drawer (see handleSessionClick).
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');

  const [cohort, setCohort] = useState<CoachCohortDetail | null>(null);
  const [loading, setLoading] = useState(true);

  // Mark Attendance modal state
  const [attendanceModalOpened, setAttendanceModalOpened] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedSessionMeta, setSelectedSessionMeta] = useState<{
    sessionNumber: number | null;
    topicName: string | null;
    groupName: string;
    scheduledStart: string | null;
    totalSessions: number;
    sessionStatus: string;
  } | null>(null);
  const [bannerRefreshKey, setBannerRefreshKey] = useState(0);
  // MED-1360: a 1:1 to open in the 1:1 drawer of the Sessions tab.
  const [oneOnOneRequestId, setOneOnOneRequestId] = useState<string | null>(null);

  // Huddle state (MED-268)
  const [huddles, setHuddles] = useState<CoachHuddle[]>([]);
  const [huddlesLoading, setHuddlesLoading] = useState(false);
  const [addHuddleOpened, { open: openAddHuddle, close: closeAddHuddle }] = useDisclosure(false);
  const [huddleDetailOpened, { open: openHuddleDetail, close: closeHuddleDetail }] =
    useDisclosure(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedHuddleId, setSelectedHuddleId] = useState<string | null>(null);
  const [editHuddle, setEditHuddle] = useState<CoachHuddle | null>(null);
  const [groupParticipants, setGroupParticipants] = useState<HuddleParticipantOption[]>([]);
  const [cancelConfirmOpened, { open: openCancelConfirm, close: closeCancelConfirm }] =
    useDisclosure(false);
  const [cancelHuddleTarget, setCancelHuddleTarget] = useState<CoachHuddle | null>(null);

  // Survey results modal state
  const [surveyResultsOpened, { open: openSurveyResults, close: closeSurveyResults }] =
    useDisclosure(false);
  const [surveyResultsGroupId, setSurveyResultsGroupId] = useState<string | undefined>();
  const [surveyResultsGroupName, setSurveyResultsGroupName] = useState<string | undefined>();
  const [surveyResultsInitialTab, setSurveyResultsInitialTab] = useState<
    'post_session' | 'midpoint' | 'endpoint' | undefined
  >();

  // Coach feedback pane state (MED-330)
  const [feedbackPaneOpen, setFeedbackPaneOpen] = useState(false);
  const [feedbackSurveyType, setFeedbackSurveyType] = useState<'midpoint' | 'endpoint'>('midpoint');

  const handleOpenCoachFeedback = useCallback((surveyType: 'midpoint' | 'endpoint') => {
    setFeedbackSurveyType(surveyType);
    setFeedbackPaneOpen(true);
  }, []);

  // Deep link: auto-open feedback pane from email magic link (MED-330)
  useEffect(() => {
    if (isPartnerCoach) return; // Partner coaches don't have feedback
    const surveyParam = searchParams.get('survey');
    const openParam = searchParams.get('open');
    if (surveyParam && openParam === 'true' && cohort) {
      setFeedbackPaneOpen(true);
      setSearchParams((prev) => {
        prev.delete('survey');
        prev.delete('open');
        return prev;
      });
    }
  }, [cohort, searchParams, setSearchParams, isPartnerCoach]);

  // Deep link: auto-open attendance modal from post-session email (E24-4)
  const [deepLinkSessionId] = useState<string | null>(() => searchParams.get('session'));
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);

  // Tab state from URL
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<string | null>(tabFromUrl || 'sessions');

  // MED-1367: Participants / Groups sub-view of the Participants & Groups tab,
  // kept in ?view= the same as the CM cohort page.
  const participantsView: CoachParticipantsView =
    searchParams.get('view') === 'groups' ? 'groups' : 'participants';

  const handleParticipantsViewChange = useCallback(
    (view: CoachParticipantsView) => {
      setSearchParams(
        view === 'groups' ? { tab: 'participants', view } : { tab: 'participants' },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Handle tab change - update URL
  const handleTabChange = useCallback(
    (value: string | null) => {
      setActiveTab(value);
      if (value && value !== 'sessions') {
        setSearchParams({ tab: value }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    },
    [setSearchParams],
  );

  // Fetch cohort data
  useEffect(() => {
    const fetchCohort = async () => {
      if (!cohortId) return;

      try {
        setLoading(true);
        const data = await coachPortalApi.getCohortById(cohortId);
        setCohort(data);
      } catch (error) {
        console.error('Failed to fetch cohort:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to load cohort details. Please try again.',
          color: 'red',
        });
        navigate('/coach/cohorts');
      } finally {
        setLoading(false);
      }
    };

    fetchCohort();
  }, [cohortId, navigate]);

  // Fetch huddles (MED-268)
  const loadHuddles = useCallback(async () => {
    if (!cohortId) return;
    setHuddlesLoading(true);
    try {
      const data = await coachPortalApi.getHuddles(cohortId);
      setHuddles(data);
    } catch {
      // Silently handle - empty state will show
    } finally {
      setHuddlesLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    if (cohortId && !isPartnerCoach) {
      loadHuddles();
    }
  }, [cohortId, loadHuddles, isPartnerCoach]);

  // MED-1360: a session that is not in the coach's group lists. A 1:1 never
  // is (the cohort payload leaves 1:1s out of group sessions), and it must
  // open the 1:1 drawer: the group drawer starts every participant on
  // Attended, and a mark triggers the coach payment. Other sessions keep the
  // group drawer, as before.
  const openSessionOutsideGroups = useCallback(
    async (sessionId: string) => {
      if (!cohortId) return;
      try {
        const session = await sessionsApi.getById(cohortId, sessionId);
        if (session.session_type === 'one_on_one_session') {
          setOneOnOneRequestId(sessionId);
          return;
        }
      } catch (error) {
        console.error('Failed to load session:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to load the session. Please try again.',
          color: 'red',
        });
        return;
      }
      setSelectedSessionId(sessionId);
      setAttendanceModalOpened(true);
    },
    [cohortId],
  );

  const handleOneOnOneRequestHandled = useCallback(() => setOneOnOneRequestId(null), []);
  const handleOneOnOneAttendanceChanged = useCallback(
    () => setBannerRefreshKey((k) => k + 1),
    [],
  );

  // Handle session click - open mark attendance modal (E8-10)
  const handleSessionClick = useCallback(
    (sessionId: string) => {
      // Find session metadata from cohort groups
      if (cohort) {
        let inGroup = false;
        for (const group of cohort.groups) {
          const session = group.sessions.find((s) => s.id === sessionId);
          if (session) {
            inGroup = true;
            // MED-1152: route completed Live Session Delivery sessions to the
            // unified post-session page (CoachPostSessionPage) instead of the
            // legacy PostSessionModal drawer. The post-session page shows the
            // in-session notes (sessions.coach_session_notes_html) the coach
            // took during the live session — the legacy drawer reads a
            // different field, which is why those notes never appeared there
            // (the reported UAT bug, AC4). This applies to ANY completed
            // session the coach opens, not just the most-recent one surfaced
            // by the `session_report_ready` nudge (AC3), and mirrors the
            // peer-leader return path (MED-1145) so every Live Session
            // Delivery leader revisits a session the same way (AC6). Non-LSD
            // sessions (flag off) and non-completed sessions keep the legacy
            // drawer unchanged (AC2).
            //
            // MED-1241: this branch must AND-in the PER-COHORT LSD gate too, not
            // just the global `live_session_delivery` flag. The global flag has
            // been ON since GA, so on a classic MED01 cohort (per-cohort LSD
            // off) this used to route to CoachPostSessionPage, whose MED-1181
            // defense-in-depth gate then dead-ends the coach on the "feature
            // isn't available yet" alert — the coach could no longer reach past
            // attendance (regression from MED-1152). `CoachCohortDetail` is a
            // flat payload: its per-cohort gate is the server-provided
            // `live_session_delivery_enabled` boolean (coachPortal.ts, added by
            // MED-1181 for exactly this page), NOT a `modalities` object array —
            // so we gate on that field directly rather than the
            // `isLiveSessionDeliveryEnabled(cohort)` selector, which only fits
            // sites that carry a full `modalities` array (OverviewTab /
            // CohortDetailPage). This also covers the ?session= deep-link path,
            // which routes through this same callback (AC5).
            if (
              liveSessionDeliveryEnabled &&
              cohort.live_session_delivery_enabled &&
              session.status === 'completed'
            ) {
              navigate(`/coach/cohorts/${cohortId}/sessions/${sessionId}/post-session`);
              return;
            }
            setSelectedSessionMeta({
              sessionNumber: session.session_number,
              topicName: session.topic_name,
              groupName: group.name,
              scheduledStart: session.scheduled_start,
              totalSessions: group.sessions.length,
              sessionStatus: session.status,
            });
            break;
          }
        }
        if (!inGroup) {
          void openSessionOutsideGroups(sessionId);
          return;
        }
      }
      setSelectedSessionId(sessionId);
      setAttendanceModalOpened(true);
    },
    [cohort, liveSessionDeliveryEnabled, navigate, cohortId, openSessionOutsideGroups],
  );

  // Deep link: auto-open attendance modal when ?session= param is present (E24-4)
  useEffect(() => {
    if (deepLinkSessionId && cohort && !deepLinkHandled) {
      setDeepLinkHandled(true);
      setSearchParams((prev) => {
        prev.delete('session');
        return prev;
      });
      handleSessionClick(deepLinkSessionId);
    }
  }, [deepLinkSessionId, cohort, deepLinkHandled, setSearchParams, handleSessionClick]);

  // Handle attendance modal close
  const handleAttendanceModalClose = useCallback(() => {
    setAttendanceModalOpened(false);
    setSelectedSessionId(null);
    setSelectedSessionMeta(null);
  }, []);

  // Handle attendance success - refresh cohort data and banner
  const handleAttendanceSuccess = useCallback(async () => {
    setBannerRefreshKey((k) => k + 1);
    if (cohortId) {
      try {
        const data = await coachPortalApi.getCohortById(cohortId);
        setCohort(data);
      } catch (error) {
        console.error('Failed to refresh cohort:', error);
      }
    }
  }, [cohortId]);

  // Handle mark attendance from banner
  const handleMarkAttendanceFromBanner = useCallback(
    (session: SessionNeedingAttendance) => {
      handleSessionClick(session.id);
    },
    [handleSessionClick],
  );

  // Load participants for a group
  const loadGroupParticipants = useCallback(
    async (groupId: string) => {
      if (!cohort) return;
      const group = cohort.groups.find((g) => g.id === groupId);
      if (group && group.sessions.length > 0) {
        try {
          const sessionData = await coachPortalApi.getSessionParticipants(group.sessions[0].id);
          setGroupParticipants(
            sessionData.participants.map((p) => ({
              user_id: p.user_id,
              participant_id: p.user_id,
              first_name: p.first_name,
              last_name: p.last_name,
              email: p.email,
            })),
          );
        } catch {
          setGroupParticipants([]);
        }
      } else {
        setGroupParticipants([]);
      }
    },
    [cohort],
  );

  // Handle group change from modal dropdown
  const handleHuddleGroupChange = useCallback(
    async (groupId: string) => {
      setSelectedGroupId(groupId);
      await loadGroupParticipants(groupId);
    },
    [loadGroupParticipants],
  );

  // Huddle handlers (MED-268)
  const handleAddHuddle = useCallback(
    async () => {
      if (!cohort) return;

      // Default to first group and load its participants
      const firstGroup = cohort.groups[0];
      if (firstGroup) {
        setSelectedGroupId(firstGroup.id);
        await loadGroupParticipants(firstGroup.id);
      } else {
        setSelectedGroupId(null);
        setGroupParticipants([]);
      }

      setEditHuddle(null);
      openAddHuddle();
    },
    [cohort, openAddHuddle, loadGroupParticipants],
  );

  const handleHuddleClick = useCallback(
    (huddleId: string) => {
      setSelectedHuddleId(huddleId);
      openHuddleDetail();
    },
    [openHuddleDetail],
  );

  const handleEditHuddle = useCallback(
    async (huddle: CoachHuddle) => {
      if (!cohort) return;

      setSelectedGroupId(huddle.group_id);
      await loadGroupParticipants(huddle.group_id);
      setEditHuddle(huddle);
      openAddHuddle();
    },
    [cohort, openAddHuddle, loadGroupParticipants],
  );

  const handleCancelHuddle = useCallback(
    (huddle: CoachHuddle) => {
      setCancelHuddleTarget(huddle);
      openCancelConfirm();
    },
    [openCancelConfirm],
  );

  const confirmCancelHuddle = useCallback(async () => {
    if (!cancelHuddleTarget) return;

    try {
      await coachPortalApi.cancelHuddle(cancelHuddleTarget.id);
      notifications.show({
        title: 'Check In Cancelled',
        message: 'The check in has been cancelled.',
        color: 'green',
      });
      loadHuddles();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to cancel check in.',
        color: 'red',
      });
    } finally {
      closeCancelConfirm();
      setCancelHuddleTarget(null);
    }
  }, [cancelHuddleTarget, loadHuddles, closeCancelConfirm]);

  const handleHuddleSuccess = useCallback(() => {
    loadHuddles();
  }, [loadHuddles]);

  // Survey results handler
  const handleOpenSurveyResults = useCallback(
    (groupId: string, groupName: string, tab?: 'post_session' | 'midpoint' | 'endpoint') => {
      setSurveyResultsGroupId(groupId);
      setSurveyResultsGroupName(groupName);
      setSurveyResultsInitialTab(tab);
      openSurveyResults();
    },
    [openSurveyResults],
  );

  if (loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!cohort) {
    return null;
  }

  // MED-778: Show features-disabled page if coach features are OFF for this cohort
  if (cohort.coach_features_enabled === false) {
    return <FeaturesDisabledPage />;
  }

  const statusConfig = COHORT_STATUS_CONFIG[cohort.status as keyof typeof COHORT_STATUS_CONFIG];

  return (
    <Container size="lg" py="xl">
      {/* Header */}
      <Group justify="space-between" align="flex-start" mb="xl">
        <Group gap="md" align="flex-start" wrap="nowrap">
          <CircleBackButton
            onClick={() => navigate('/coach/cohorts')}
            aria-label="Back to My Cohorts"
          />
          <Box>
            <Group gap="md" mb="xs">
              <Title order={2}>{cohort.name}</Title>
              <Badge color={statusConfig?.color || 'gray'} variant={statusConfig?.variant ?? 'light'} size="lg">
                {statusConfig?.label || cohort.status}
              </Badge>
            </Group>
            <Text c="dimmed">
              {cohort.partner_name || 'No Partner'} · {cohort.code || 'No Code'}
            </Text>
            {cohort.start_date && cohort.end_date && (
              <Text size="sm" c="dimmed" mt={4}>
                {formatDateOnlyRange(cohort.start_date, cohort.end_date)}
              </Text>
            )}
          </Box>
        </Group>
      </Group>

      {/* Attendance Needed Banner */}
      <AttendanceNeededBanner
        key={`attendance-${bannerRefreshKey}`}
        cohortId={cohortId}
        onMarkAttendance={handleMarkAttendanceFromBanner}
      />

      {/* MED-330: Purple feedback callout banner below attendance (hidden for partner coaches) */}
      {!isPartnerCoach && (
        <FeedbackNeededBanner
          key={`feedback-${bannerRefreshKey}`}
          cohortId={cohort.id}
          onCompleteFeedback={handleOpenCoachFeedback}
        />
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tabs.List mb="lg">
          <Tabs.Tab value="sessions">Sessions</Tabs.Tab>
          {/* MED-1367: shown to medley and partner coaches alike. */}
          <Tabs.Tab value="participants">Participants &amp; Groups</Tabs.Tab>
          {!isPartnerCoach && <Tabs.Tab value="resources">Resources</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="sessions">
          <SessionsTabContent
            cohort={cohort}
            onSessionClick={handleSessionClick}
            huddles={isPartnerCoach ? [] : huddles}
            huddlesLoading={isPartnerCoach ? false : huddlesLoading}
            onAddHuddle={handleAddHuddle}
            onHuddleClick={handleHuddleClick}
            onEditHuddle={handleEditHuddle}
            onCancelHuddle={handleCancelHuddle}
            onOpenSurveyResults={isPartnerCoach ? undefined : handleOpenSurveyResults}
            onOpenCoachFeedback={isPartnerCoach ? undefined : handleOpenCoachFeedback}
            isPartnerCoach={isPartnerCoach}
            openOneOnOneSessionId={oneOnOneRequestId}
            onOneOnOneSessionOpened={handleOneOnOneRequestHandled}
            onOneOnOneAttendanceChanged={handleOneOnOneAttendanceChanged}
          />
        </Tabs.Panel>

        <Tabs.Panel value="participants">
          {/* Mounted only while open, so the page load makes no extra calls. */}
          {activeTab === 'participants' && (
            <CoachParticipantsTabContent
              cohort={cohort}
              view={participantsView}
              onViewChange={handleParticipantsViewChange}
              onSessionClick={handleSessionClick}
            />
          )}
        </Tabs.Panel>

        {!isPartnerCoach && (
          <Tabs.Panel value="resources">
            <ResourcesTabContent cohortId={cohort.id} />
          </Tabs.Panel>
        )}
      </Tabs>

      {/* Post-Session Modal (Attendance + Feedback) */}
      <PostSessionModal
        opened={attendanceModalOpened}
        onClose={handleAttendanceModalClose}
        sessionId={selectedSessionId}
        onSuccess={handleAttendanceSuccess}
        sessionNumber={selectedSessionMeta?.sessionNumber}
        topicName={selectedSessionMeta?.topicName}
        groupName={selectedSessionMeta?.groupName}
        scheduledStart={selectedSessionMeta?.scheduledStart}
        totalSessions={selectedSessionMeta?.totalSessions}
        sessionStatus={selectedSessionMeta?.sessionStatus}
      />

      {/* Add/Edit Huddle Modal (MED-268, hidden for partner coaches) */}
      {!isPartnerCoach && cohortId && selectedGroupId && (
        <AddHuddleModal
          opened={addHuddleOpened}
          onClose={closeAddHuddle}
          onSuccess={handleHuddleSuccess}
          cohortId={cohortId}
          groupId={selectedGroupId}
          groups={cohort.groups.map((g) => ({ id: g.id, name: g.name }))}
          participants={groupParticipants}
          onGroupChange={handleHuddleGroupChange}
          defaultMeetingPlatform={cohort?.default_meeting_platform}
          defaultVideoLink={cohort?.default_meeting_link || undefined}
          coachMeetingLinks={cohort ? {
            zoom_link: cohort.coach_zoom_link || null,
            teams_link: cohort.coach_teams_link || null,
            google_meet_link: cohort.coach_google_meet_link || null,
          } : null}
          editHuddle={editHuddle}
        />
      )}

      {/* Huddle Detail Modal (MED-268, hidden for partner coaches) */}
      {!isPartnerCoach && (
        <HuddleDetailModal
          opened={huddleDetailOpened}
          onClose={closeHuddleDetail}
          huddleId={selectedHuddleId}
          onSuccess={handleHuddleSuccess}
        />
      )}

      {!isPartnerCoach && (
        <Modal
          opened={cancelConfirmOpened}
          onClose={closeCancelConfirm}
          title="Cancel Check In"
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>Are you sure you want to cancel this check in?</Text>
            {cancelHuddleTarget && (
              <Text size="sm" c="dimmed">
                {cancelHuddleTarget.title} with {cancelHuddleTarget.participant_name}
              </Text>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={closeCancelConfirm}>
                No, Keep It
              </Button>
              <Button color="red" onClick={confirmCancelHuddle}>
                Yes, Cancel Check In
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {/* Coach Feedback Side Pane (MED-330, hidden for partner coaches) */}
      {!isPartnerCoach && (
        <CoachFeedbackPane
          opened={feedbackPaneOpen}
          onClose={() => setFeedbackPaneOpen(false)}
          cohortId={cohort.id}
          cohortName={cohort.name}
          surveyType={feedbackSurveyType}
          onSuccess={() => setBannerRefreshKey((k) => k + 1)}
        />
      )}

      {/* Survey Results Modal (read-only, hidden for partner coaches) */}
      {!isPartnerCoach && (
        <SurveyResultsModal
          opened={surveyResultsOpened}
          onClose={closeSurveyResults}
          cohortId={cohort.id}
          groupId={surveyResultsGroupId}
          groupName={surveyResultsGroupName}
          initialTab={surveyResultsInitialTab}
        />
      )}
    </Container>
  );
}

export default CoachCohortDetailPage;
